import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Image as ImageIcon,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  Strikethrough,
  Underline as UnderlineIcon,
} from "lucide-react";
import {
  useEffect,
  useId,
  useRef,
  useState,
  type ClipboardEvent,
  type DragEvent,
  type KeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { triggerFileInput } from "@/lib/triggerFileInput";
import { clipboardHtmlKeepsLineBreaks, plainToHtml } from "@/mail/htmlBody";
import { handleListTab } from "@/mail/listIndent";
import {
  MAIL_LINE_SPACING_EVENT,
  MAIL_LINE_SPACING_OPTIONS,
  readMailLineSpacing,
  writeMailLineSpacing,
  type MailLineSpacingId,
} from "@/mail/prefs";

type Props = {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  /** When true, body cannot take focus (discard dialog open). */
  locked?: boolean;
  /** Compose focuses the body on open. Settings leaves focus alone. */
  autoFocus?: boolean;
  className?: string;
  /** Accessible name for the editing area. */
  label?: string;
  toolbarLabel?: string;
  /** When set, inserted photos are scaled down so a signature can be stored in this browser. */
  imageMaxEdge?: number;
  onImageRejected?: (message: string) => void;
};

type Align = "left" | "center" | "right";

const FONT_FACES = [
  { label: "Sans Serif", value: "Arial" },
  { label: "Serif", value: "Georgia" },
  { label: "Fixed width", value: "Courier New" },
  { label: "Verdana", value: "Verdana" },
  { label: "Tahoma", value: "Tahoma" },
  { label: "Trebuchet", value: "Trebuchet MS" },
] as const;

const FONT_SIZES = [
  { label: "Small", value: "2" },
  { label: "Normal", value: "3" },
  { label: "Large", value: "4" },
  { label: "Huge", value: "6" },
] as const;

const TEXT_COLORS = [
  { label: "Black", value: "#111111" },
  { label: "Gray", value: "#5f6368" },
  { label: "Red", value: "#d93025" },
  { label: "Blue", value: "#1a73e8" },
  { label: "Green", value: "#188038" },
] as const;

/** WebKit puts a trailing break inside a line, which draws as a blank line. */
function stripDoubledBreaks(root: HTMLElement) {
  for (const el of root.querySelectorAll("div, p")) {
    const last = el.lastChild;
    if (!last || last.nodeName !== "BR" || el.childNodes.length < 2) continue;
    last.remove();
  }
}

function runCommand(command: string, value?: string) {
  // Email HTML needs classic rich-text commands; fine for Chromium Hub.
  document.execCommand(command, false, value);
}

function selectionInside(editor: HTMLElement | null): Range | null {
  if (!editor) return null;
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return null;
  const range = sel.getRangeAt(0);
  if (!editor.contains(range.commonAncestorContainer)) return null;
  return range.cloneRange();
}

function withProtocol(href: string): string {
  const trimmed = href.trim();
  if (!trimmed) return "";
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
}

function looksLikeUrl(text: string): boolean {
  const t = text.trim();
  if (!t) return false;
  if (/^https?:\/\//i.test(t)) return true;
  if (/^www\./i.test(t)) return true;
  return /^[a-z0-9.-]+\.[a-z]{2,}([/?#].*)?$/i.test(t);
}

/** Anchor under caret, or the only anchor fully covering the selection. */
function findLinkNearSelection(editor: HTMLElement | null): HTMLAnchorElement | null {
  if (!editor) return null;
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return null;
  const range = sel.getRangeAt(0);
  if (!editor.contains(range.commonAncestorContainer)) return null;

  const fromNode = (node: Node | null): HTMLAnchorElement | null => {
    let walk: Node | null = node;
    while (walk && walk !== editor) {
      if (walk.nodeName === "A") return walk as HTMLAnchorElement;
      walk = walk.parentNode;
    }
    return null;
  };

  const atStart = fromNode(range.startContainer);
  if (range.collapsed) return atStart;

  const atEnd = fromNode(range.endContainer);
  if (atStart && atStart === atEnd) return atStart;

  const ancestor = range.commonAncestorContainer;
  return fromNode(ancestor);
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error || new Error("Could not read image."));
    reader.readAsDataURL(file);
  });
}

function isImageFile(file: File): boolean {
  return file.type.startsWith("image/") || /\.(png|jpe?g|gif|webp|bmp|heic|heif)$/i.test(file.name);
}

function shrinkImageDataUrl(dataUrl: string, maxEdge: number): Promise<string> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const longest = Math.max(img.width, img.height) || 1;
      const scale = Math.min(1, maxEdge / longest);
      const width = Math.max(1, Math.round(img.width * scale));
      const height = Math.max(1, Math.round(img.height * scale));
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        resolve(dataUrl);
        return;
      }
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL("image/jpeg", 0.82));
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
}

export function MailComposeEditor({
  value,
  onChange,
  placeholder = "Write your message…",
  locked = false,
  autoFocus = true,
  className,
  label = "Message body",
  toolbarLabel = "Message formatting",
  imageMaxEdge,
  onImageRejected,
}: Props) {
  const editorRef = useRef<HTMLDivElement | null>(null);
  const imageInputRef = useRef<HTMLInputElement | null>(null);
  const imageInputId = useId();
  const focusedRef = useRef(false);
  const savedRangeRef = useRef<Range | null>(null);
  const editingLinkRef = useRef<HTMLAnchorElement | null>(null);
  const selectedImgRef = useRef<HTMLImageElement | null>(null);
  const resizeStartRef = useRef<{ x: number; width: number } | null>(null);
  const [active, setActive] = useState({
    bold: false,
    italic: false,
    underline: false,
    ul: false,
    ol: false,
    align: "left" as Align,
    link: false,
  });
  const [colorOpen, setColorOpen] = useState(false);
  const [spacing, setSpacing] = useState<MailLineSpacingId>(() => readMailLineSpacing());
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkEditing, setLinkEditing] = useState(false);
  const [linkText, setLinkText] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [selectedImg, setSelectedImg] = useState<HTMLImageElement | null>(null);
  const [imgWidth, setImgWidth] = useState(0);

  function placeCaretAtStart(el: HTMLElement) {
    el.focus();
    const sel = window.getSelection();
    if (!sel) return;
    const range = document.createRange();
    const first = el.firstChild;
    if (first && first.nodeType === Node.ELEMENT_NODE) {
      const block = first as HTMLElement;
      if (block.childNodes.length) {
        range.setStart(block, 0);
      } else {
        range.selectNodeContents(block);
      }
      range.collapse(true);
    } else if (first) {
      range.setStart(first, 0);
      range.collapse(true);
    } else {
      range.selectNodeContents(el);
      range.collapse(true);
    }
    sel.removeAllRanges();
    sel.addRange(range);
    focusedRef.current = true;
  }

  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    if (focusedRef.current) return;
    if (el.innerHTML !== (value || "")) {
      el.innerHTML = value || "";
    }
  }, [value]);

  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    if (value) el.innerHTML = value;
    if (locked || !autoFocus) return;
    const id = window.requestAnimationFrame(() => {
      placeCaretAtStart(el);
    });
    return () => window.cancelAnimationFrame(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only; key remounts per draft
  }, []);

  useEffect(() => {
    function syncSpacing() {
      setSpacing(readMailLineSpacing());
    }
    window.addEventListener(MAIL_LINE_SPACING_EVENT, syncSpacing);
    return () => window.removeEventListener(MAIL_LINE_SPACING_EVENT, syncSpacing);
  }, []);

  useEffect(() => {
    if (!locked) return;
    const el = editorRef.current;
    if (el && document.activeElement === el) el.blur();
    const sel = window.getSelection();
    if (sel && sel.rangeCount) sel.removeAllRanges();
    focusedRef.current = false;
    clearImageSelection();
  }, [locked]);

  function clearImageSelection() {
    const prev = selectedImgRef.current;
    if (prev) prev.removeAttribute("data-hub-selected");
    selectedImgRef.current = null;
    setSelectedImg(null);
    setImgWidth(0);
    resizeStartRef.current = null;
  }

  function selectImage(img: HTMLImageElement) {
    const editor = editorRef.current;
    if (!editor || !editor.contains(img)) return;
    const prev = selectedImgRef.current;
    if (prev && prev !== img) prev.removeAttribute("data-hub-selected");
    img.setAttribute("data-hub-selected", "1");
    if (!img.style.width && img.naturalWidth) {
      const max = Math.min(img.naturalWidth, editor.clientWidth || img.naturalWidth);
      img.style.width = `${max}px`;
      img.style.height = "auto";
    }
    selectedImgRef.current = img;
    setSelectedImg(img);
    setImgWidth(Math.round(img.getBoundingClientRect().width) || img.naturalWidth || 0);
  }

  function emit() {
    const editor = editorRef.current;
    if (editor) stripDoubledBreaks(editor);
    const html = editor?.innerHTML || "";
    onChange(html);
    refreshActive();
  }

  function refreshActive() {
    const inLink = Boolean(findLinkNearSelection(editorRef.current));
    setActive({
      bold: document.queryCommandState("bold"),
      italic: document.queryCommandState("italic"),
      underline: document.queryCommandState("underline"),
      ul: document.queryCommandState("insertUnorderedList"),
      ol: document.queryCommandState("insertOrderedList"),
      align: document.queryCommandState("justifyCenter")
        ? "center"
        : document.queryCommandState("justifyRight")
          ? "right"
          : "left",
      link: inLink || linkOpen,
    });
  }

  function focusEditor() {
    editorRef.current?.focus();
    try {
      document.execCommand("defaultParagraphSeparator", false, "div");
      document.execCommand("styleWithCSS", false, "true");
    } catch {
      /* older shells ignore these */
    }
  }

  function rememberSelection() {
    const range = selectionInside(editorRef.current);
    if (range) savedRangeRef.current = range;
  }

  function applyStyled(command: string, arg?: string) {
    if (!restoreSelection()) focusEditor();
    try {
      document.execCommand("styleWithCSS", false, "true");
    } catch {
      /* ignore */
    }
    runCommand(command, arg);
    emit();
  }

  function restoreSelection(): boolean {
    const range = savedRangeRef.current;
    const editor = editorRef.current;
    if (!range || !editor) return false;
    focusEditor();
    const sel = window.getSelection();
    if (!sel) return false;
    sel.removeAllRanges();
    sel.addRange(range);
    return true;
  }

  function apply(command: string, arg?: string) {
    focusEditor();
    runCommand(command, arg);
    emit();
  }

  function setAlign(next: Align) {
    focusEditor();
    if (next === "center") runCommand("justifyCenter");
    else if (next === "right") runCommand("justifyRight");
    else runCommand("justifyLeft");
    emit();
  }

  function closeLinkDialog() {
    setLinkOpen(false);
    setLinkEditing(false);
    setLinkText("");
    setLinkUrl("");
    savedRangeRef.current = null;
    editingLinkRef.current = null;
    refreshActive();
  }

  function openLinkDialog() {
    const editor = editorRef.current;
    const range = selectionInside(editor);
    const existing = findLinkNearSelection(editor);

    if (existing && editor?.contains(existing)) {
      editingLinkRef.current = existing;
      const wrap = document.createRange();
      wrap.selectNode(existing);
      savedRangeRef.current = wrap;
      setLinkEditing(true);
      setLinkText(existing.textContent || "");
      setLinkUrl(existing.getAttribute("href") || "");
      setLinkOpen(true);
      refreshActive();
      return;
    }

    editingLinkRef.current = null;
    savedRangeRef.current = range;
    setLinkEditing(false);
    const selected = range && !range.collapsed ? range.toString() : "";
    setLinkText(selected);
    setLinkUrl(looksLikeUrl(selected) ? withProtocol(selected) : "");
    setLinkOpen(true);
    refreshActive();
  }

  function placeCaretOnNewLineAfter(link: HTMLAnchorElement | null) {
    const editor = editorRef.current;
    if (!editor) return;

    let anchor = link;
    if (!anchor) {
      anchor = findLinkNearSelection(editor);
    }
    if (!anchor || !editor.contains(anchor)) return;

    const br = document.createElement("br");
    anchor.after(br);
    const holder = document.createTextNode("\u200b");
    br.after(holder);

    const caret = document.createRange();
    caret.setStart(holder, 0);
    caret.collapse(true);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(caret);
    focusEditor();
  }

  function applyLink() {
    const href = withProtocol(linkUrl);
    if (!href) return;

    const editor = editorRef.current;
    if (!editor) return;

    const label = (linkText.trim() || href).replace(/\s+/g, " ");
    focusEditor();

    const editing = editingLinkRef.current;
    if (linkEditing && editing && editor.contains(editing)) {
      editing.setAttribute("href", href);
      editing.href = href;
      editing.setAttribute("target", "_blank");
      editing.setAttribute("rel", "noopener noreferrer");
      editing.textContent = label;
      placeCaretOnNewLineAfter(editing);
      emit();
      closeLinkDialog();
      return;
    }

    restoreSelection();
    const range = savedRangeRef.current;
    const anchor = document.createElement("a");
    anchor.href = href;
    anchor.setAttribute("target", "_blank");
    anchor.setAttribute("rel", "noopener noreferrer");
    anchor.textContent = label;

    if (range) {
      const live = range.cloneRange();
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(live);
      live.deleteContents();
      live.insertNode(anchor);
    } else {
      const end = document.createRange();
      end.selectNodeContents(editor);
      end.collapse(false);
      end.insertNode(anchor);
    }

    placeCaretOnNewLineAfter(anchor);
    emit();
    closeLinkDialog();
  }

  async function insertImageFile(file: File) {
    if (!isImageFile(file)) return;
    const editor = editorRef.current;
    if (!editor || locked) return;
    const originalUrl = await readFileAsDataUrl(file);
    if (!originalUrl.startsWith("data:")) return;
    const dataUrl = imageMaxEdge ? await shrinkImageDataUrl(originalUrl, imageMaxEdge) : originalUrl;
    if (imageMaxEdge && dataUrl.length > 120_000) {
      onImageRejected?.("That image is too large for a signature. Use a smaller logo.");
      return;
    }
    focusEditor();
    const range = selectionInside(editor) || (() => {
      const end = document.createRange();
      end.selectNodeContents(editor);
      end.collapse(false);
      return end;
    })();
    const img = document.createElement("img");
    img.src = dataUrl;
    img.alt = file.name || "Image";
    img.setAttribute("data-filename", file.name || "image.png");
    img.style.maxWidth = "100%";
    img.style.height = "auto";
    const shellWidth = editor.clientWidth || 480;
    img.style.width = `${Math.min(480, Math.max(160, shellWidth - 8))}px`;
    range.deleteContents();
    range.insertNode(img);
    const after = document.createRange();
    after.setStartAfter(img);
    after.collapse(true);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(after);
    selectImage(img);
    emit();
  }

  async function onPickImages(files: FileList | null) {
    if (!files?.length) return;
    for (const file of Array.from(files)) {
      if (isImageFile(file)) await insertImageFile(file);
    }
    if (imageInputRef.current) imageInputRef.current.value = "";
  }

  function onEditorKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key !== "Tab" || event.altKey || event.ctrlKey || event.metaKey) return;
    if (event.nativeEvent.isComposing) return;
    const editor = editorRef.current;
    if (!editor || locked) return;
    const before = editor.innerHTML;
    if (!handleListTab(editor, event.shiftKey)) return;
    event.preventDefault();
    if (editor.innerHTML !== before) emit();
  }

  function onEditorPaste(event: ClipboardEvent<HTMLDivElement>) {
    const clipboard = event.clipboardData;
    const items = clipboard?.items;
    if (items?.length) {
      const images: File[] = [];
      for (const item of Array.from(items)) {
        if (item.kind === "file" && item.type.startsWith("image/")) {
          const file = item.getAsFile();
          if (file) images.push(file);
        }
      }
      if (images.length) {
        event.preventDefault();
        void (async () => {
          for (const file of images) await insertImageFile(file);
        })();
        return;
      }
    }
    if (locked) return;
    const text = clipboard?.getData("text/plain") || "";
    const html = clipboard?.getData("text/html") || "";
    if (!text || clipboardHtmlKeepsLineBreaks(html, text)) return;
    event.preventDefault();
    focusEditor();
    runCommand("insertHTML", plainToHtml(text));
    emit();
  }

  function onEditorDrop(event: DragEvent<HTMLDivElement>) {
    const files = event.dataTransfer?.files;
    if (!files?.length) return;
    const images = Array.from(files).filter(isImageFile);
    if (!images.length) return;
    event.preventDefault();
    void (async () => {
      for (const file of images) await insertImageFile(file);
    })();
  }

  function onEditorClick(event: ReactMouseEvent<HTMLDivElement>) {
    const target = event.target;
    if (target instanceof HTMLImageElement && editorRef.current?.contains(target)) {
      event.preventDefault();
      selectImage(target);
      return;
    }
    if (!(target as HTMLElement).closest?.(".mail-compose-img-resize")) {
      clearImageSelection();
    }
  }

  function onResizePointerDown(event: ReactPointerEvent<HTMLButtonElement>) {
    const img = selectedImgRef.current;
    if (!img) return;
    event.preventDefault();
    event.stopPropagation();
    resizeStartRef.current = {
      x: event.clientX,
      width: img.getBoundingClientRect().width,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  }

  function onResizePointerMove(event: ReactPointerEvent<HTMLButtonElement>) {
    const img = selectedImgRef.current;
    const start = resizeStartRef.current;
    const editor = editorRef.current;
    if (!img || !start || !editor) return;
    const delta = event.clientX - start.x;
    const max = Math.max(120, editor.clientWidth - 8);
    const next = Math.round(Math.min(max, Math.max(80, start.width + delta)));
    img.style.width = `${next}px`;
    img.style.height = "auto";
    setImgWidth(next);
  }

  function onResizePointerUp(event: ReactPointerEvent<HTMLButtonElement>) {
    resizeStartRef.current = null;
    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch {
      /* ignore */
    }
    emit();
  }

  function setSelectedWidth(next: number) {
    const img = selectedImgRef.current;
    const editor = editorRef.current;
    if (!img || !editor) return;
    const max = Math.max(120, editor.clientWidth - 8);
    const width = Math.round(Math.min(max, Math.max(80, next)));
    img.style.width = `${width}px`;
    img.style.height = "auto";
    setImgWidth(width);
    emit();
  }

  const empty = !value || value === "<br>" || value === "<div><br></div>";
  const canAddLink = Boolean(linkUrl.trim());
  const imgRect = selectedImg?.getBoundingClientRect();
  const shellRect = editorRef.current?.closest(".mail-compose-body-shell")?.getBoundingClientRect();
  const handleStyle =
    imgRect && shellRect
      ? {
          left: imgRect.right - shellRect.left - 8,
          top: imgRect.bottom - shellRect.top - 8,
        }
      : null;

  return (
    <div className={className ? `mail-compose-editor ${className}` : "mail-compose-editor"}>
      <div className="mail-compose-format-bar" role="toolbar" aria-label={toolbarLabel}>
        <select
          className="mail-compose-font"
          aria-label="Font"
          title="Font"
          defaultValue="Arial"
          onMouseDown={rememberSelection}
          onChange={(event) => applyStyled("fontName", event.target.value)}
        >
          {FONT_FACES.map((font) => (
            <option key={font.value} value={font.value}>
              {font.label}
            </option>
          ))}
        </select>
        <select
          className="mail-compose-size"
          aria-label="Text size"
          title="Text size"
          defaultValue="3"
          onMouseDown={rememberSelection}
          onChange={(event) => applyStyled("fontSize", event.target.value)}
        >
          {FONT_SIZES.map((size) => (
            <option key={size.value} value={size.value}>
              {size.label}
            </option>
          ))}
        </select>
        <select
          className="mail-compose-spacing"
          aria-label="Line spacing"
          title="Line spacing"
          value={spacing}
          onChange={(event) => {
            const next = event.target.value as MailLineSpacingId;
            setSpacing(next);
            writeMailLineSpacing(next);
          }}
        >
          {MAIL_LINE_SPACING_OPTIONS.map((option) => (
            <option key={option.id} value={option.id}>
              {option.label}
            </option>
          ))}
        </select>

        <span className="mail-compose-format-sep" aria-hidden />

        <button
          type="button"
          className={`mail-compose-format-btn${active.bold ? " active" : ""}`}
          aria-label="Bold"
          title="Bold"
          aria-pressed={active.bold}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => apply("bold")}
        >
          <Bold size={16} />
        </button>
        <button
          type="button"
          className={`mail-compose-format-btn${active.italic ? " active" : ""}`}
          aria-label="Italic"
          title="Italic"
          aria-pressed={active.italic}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => apply("italic")}
        >
          <Italic size={16} />
        </button>
        <button
          type="button"
          className={`mail-compose-format-btn${active.underline ? " active" : ""}`}
          aria-label="Underline"
          title="Underline"
          aria-pressed={active.underline}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => apply("underline")}
        >
          <UnderlineIcon size={16} />
        </button>
        <button
          type="button"
          className="mail-compose-format-btn"
          aria-label="Strikethrough"
          title="Strikethrough"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => apply("strikeThrough")}
        >
          <Strikethrough size={16} />
        </button>
        <button
          type="button"
          className={`mail-compose-format-btn${colorOpen ? " active" : ""}`}
          aria-label="Text color"
          title="Text color"
          aria-pressed={colorOpen}
          onMouseDown={(e) => {
            e.preventDefault();
            rememberSelection();
          }}
          onClick={() => setColorOpen((open) => !open)}
        >
          <span className="mail-compose-color-a" aria-hidden>
            A
          </span>
        </button>

        <span className="mail-compose-format-sep" aria-hidden />

        <button
          type="button"
          className={`mail-compose-format-btn${active.align === "left" ? " active" : ""}`}
          aria-label="Align left"
          title="Align left"
          aria-pressed={active.align === "left"}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => setAlign("left")}
        >
          <AlignLeft size={16} />
        </button>
        <button
          type="button"
          className={`mail-compose-format-btn${active.align === "center" ? " active" : ""}`}
          aria-label="Align center"
          title="Align center"
          aria-pressed={active.align === "center"}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => setAlign("center")}
        >
          <AlignCenter size={16} />
        </button>
        <button
          type="button"
          className={`mail-compose-format-btn${active.align === "right" ? " active" : ""}`}
          aria-label="Align right"
          title="Align right"
          aria-pressed={active.align === "right"}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => setAlign("right")}
        >
          <AlignRight size={16} />
        </button>

        <span className="mail-compose-format-sep" aria-hidden />

        <button
          type="button"
          className={`mail-compose-format-btn${active.ul ? " active" : ""}`}
          aria-label="Bullet list"
          title="Bullet list. Tab indents, Shift+Tab outdents."
          aria-pressed={active.ul}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => apply("insertUnorderedList")}
        >
          <List size={16} />
        </button>
        <button
          type="button"
          className={`mail-compose-format-btn${active.ol ? " active" : ""}`}
          aria-label="Numbered list"
          title="Numbered list. Tab indents, Shift+Tab outdents."
          aria-pressed={active.ol}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => apply("insertOrderedList")}
        >
          <ListOrdered size={16} />
        </button>
        <button
          type="button"
          className={`mail-compose-format-btn${active.link || linkOpen ? " active" : ""}`}
          aria-label={linkEditing || active.link ? "Edit link" : "Insert link"}
          title={linkEditing || active.link ? "Edit link" : "Insert link"}
          aria-pressed={linkOpen || active.link}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            if (linkOpen) closeLinkDialog();
            else openLinkDialog();
          }}
        >
          <LinkIcon size={16} />
        </button>
        <button
          type="button"
          className={`mail-compose-format-btn mail-compose-image-trigger${locked ? " is-disabled" : ""}`}
          aria-label="Insert image"
          title="Insert image"
          disabled={locked}
          onClick={() => triggerFileInput(imageInputRef.current)}
        >
          <ImageIcon size={16} />
        </button>
      </div>

      {colorOpen ? (
        <div className="mail-compose-color-row" role="group" aria-label="Text color">
          {TEXT_COLORS.map((color) => (
            <button
              key={color.value}
              type="button"
              className="mail-compose-color-swatch"
              style={{ background: color.value }}
              aria-label={color.label}
              title={color.label}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                applyStyled("foreColor", color.value);
                setColorOpen(false);
              }}
            />
          ))}
        </div>
      ) : null}

      {linkOpen ? (
        <div className="mail-compose-link-panel" role="group" aria-label={linkEditing ? "Edit link" : "Insert link"}>
          <input
            type="text"
            className="mail-compose-control"
            value={linkText}
            onChange={(e) => setLinkText(e.target.value)}
            placeholder="Text to display"
            aria-label="Text to display"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                applyLink();
              }
              if (e.key === "Escape") closeLinkDialog();
            }}
          />
          <div className="mail-compose-link-url-row">
            <input
              type="url"
              className="mail-compose-control"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              placeholder="https://"
              aria-label="Link URL"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  applyLink();
                }
                if (e.key === "Escape") closeLinkDialog();
              }}
            />
            <button
              type="button"
              className="mail-compose-chip-btn"
              disabled={!canAddLink}
              onClick={applyLink}
            >
              {linkEditing ? "Update" : "Add"}
            </button>
          </div>
        </div>
      ) : null}

      {selectedImg && imgWidth > 0 ? (
        <div className="mail-compose-img-toolbar" role="group" aria-label="Image size">
          <span className="mail-compose-img-toolbar-label">Image width</span>
          <input
            type="range"
            min={80}
            max={Math.max(120, (editorRef.current?.clientWidth || 480) - 8)}
            value={imgWidth}
            onChange={(e) => setSelectedWidth(Number(e.target.value))}
            aria-label="Image width in pixels"
          />
          <span className="mail-compose-img-toolbar-value">{imgWidth}px</span>
          <button
            type="button"
            className="text-btn"
            onClick={() => {
              selectedImg.remove();
              clearImageSelection();
              emit();
            }}
          >
            Remove
          </button>
        </div>
      ) : null}

      <div className="mail-compose-body-shell">
        {empty ? <span className="mail-compose-placeholder">{placeholder}</span> : null}
        <div
          ref={editorRef}
          className="mail-compose-body"
          data-spacing={spacing}
          role="textbox"
          aria-multiline="true"
          aria-label={label}
          aria-disabled={locked || undefined}
          contentEditable={!locked}
          suppressContentEditableWarning
          onFocus={() => {
            if (locked) {
              editorRef.current?.blur();
              return;
            }
            focusedRef.current = true;
            refreshActive();
          }}
          onBlur={() => {
            focusedRef.current = false;
            emit();
          }}
          onInput={emit}
          onKeyDown={onEditorKeyDown}
          onKeyUp={refreshActive}
          onMouseUp={refreshActive}
          onClick={onEditorClick}
          onPaste={onEditorPaste}
          onDragOver={(e) => {
            if ([...e.dataTransfer.types].includes("Files")) e.preventDefault();
          }}
          onDrop={onEditorDrop}
        />
        {selectedImg && handleStyle ? (
          <button
            type="button"
            className="mail-compose-img-resize"
            style={{ left: handleStyle.left, top: handleStyle.top }}
            aria-label="Resize image"
            title="Drag to resize"
            onPointerDown={onResizePointerDown}
            onPointerMove={onResizePointerMove}
            onPointerUp={onResizePointerUp}
            onPointerCancel={onResizePointerUp}
          />
        ) : null}
      </div>

      <input
        id={imageInputId}
        ref={imageInputRef}
        type="file"
        accept="image/*"
        multiple
        tabIndex={-1}
        aria-hidden="true"
        className="hub-file-input-offscreen"
        disabled={locked}
        onChange={(e) => void onPickImages(e.target.files)}
      />
    </div>
  );
}
